import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { isLaunched } from "@/lib/launch";
import { useLang } from "@/lib/i18n";

const LAUNCHED = isLaunched();

function MetricsCard() {
  const ref = useRef(null);
  const { t } = useLang();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(my, { stiffness: 140, damping: 18 });
  const ry = useSpring(mx, { stiffness: 140, damping: 18 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    mx.set(px * 10);
    my.set(-py * 10);
  };

  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      data-testid="hero-metrics-card"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-2xl border border-[#262930] bg-[#121418] p-8 shadow-2xl shadow-black/40"
    >
      <div className="flex items-center justify-between border-b border-[#262930] pb-5">
        <span className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76]">
          {t("hero.cardLabel")}
        </span>
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C87A5B] opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C87A5B]" />
        </span>
      </div>
      <div className="py-8">
        <div className="font-display text-2xl sm:text-3xl font-bold text-[#EAE6E1]">
          {LAUNCHED ? t("hero.cardTitleLive") : t("hero.cardTitleBuild")}
        </div>
        <p className="mt-3 text-sm leading-relaxed text-[#9E9E9E]">
          {LAUNCHED ? t("hero.cardBodyLive") : t("hero.cardBodyBuild")}
        </p>
      </div>
      <div className="space-y-3 border-t border-[#262930] pt-5">
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#9E9E9E]">SendexPro</span>
          <span className={`font-tech text-[9px] uppercase tracking-[0.15em] ${LAUNCHED ? "text-[#4ADE80]" : "text-[#C87A5B]"}`}>
            {LAUNCHED ? t("hero.liveNow") : t("hero.comingSoon")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#9E9E9E]">{t("hero.yourVenture")}</span>
          <span className="font-tech text-[9px] uppercase tracking-[0.15em] text-[#8C9A86]">
            {t("hero.openSlot")}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { t } = useLang();
  const lines = t("hero.lines");
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const yParallax = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={sectionRef}
      data-testid="hero-section"
      className="hero-grid-bg relative overflow-hidden pt-36 pb-24 md:pt-44 md:pb-32"
    >
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full opacity-30"
        style={{ background: "radial-gradient(closest-side, rgba(140,154,134,0.22), transparent)" }}
      />
      <motion.div
        style={{ y: yParallax, opacity }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
      >
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#262930] bg-[#121418] px-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#C87A5B]" />
            <span data-testid="hero-status-chip" className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#9E9E9E]">
              {t("hero.chip")}
            </span>
          </motion.div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[1.08] text-[#EAE6E1]">
            <span className="sr-only">Multigroupnetwork — </span>
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  data-testid={`hero-heading-line-${i}`}
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? (
                    <span className="text-[#8C9A86]">{line} </span>
                  ) : (
                    <>{line} </>
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#9E9E9E]"
          >
            {t("hero.subtext")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-nowrap items-center gap-3 md:gap-4"
          >
            <button
              data-testid="hero-cta-explore-portfolio"
              onClick={() => scrollToId("portfolio")}
              className="group flex items-center gap-2 whitespace-nowrap rounded-full bg-[#EAE6E1] px-5 py-3 text-xs font-semibold text-[#0B0C0E] transition-colors duration-200 hover:bg-[#8C9A86] md:px-7 md:py-3.5 md:text-sm"
            >
              {t("hero.ctaExplore")}
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-initiate-project"
              onClick={() => scrollToId("contact")}
              className="group flex items-center gap-2 whitespace-nowrap rounded-full border border-[#3A3F4B] px-5 py-3 text-xs font-semibold text-[#EAE6E1] transition-colors duration-200 hover:border-[#8C9A86] hover:text-[#8C9A86] md:px-7 md:py-3.5 md:text-sm"
            >
              {t("hero.ctaInitiate")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <MetricsCard />
        </div>
      </motion.div>
    </section>
  );
}
